import React, { useEffect, useState } from 'react';
import { Shield, Building2, Users, Sparkles, BookOpen, Zap, Lock, Eye, EyeOff, Globe, Check, ShieldCheck, Cloud, Activity } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

/* -------------------------------------------------------------------------- */
/*  Operating Manual — institutional explainer for the platform owner +       */
/*  holding admins. Static (no LLM), reads MRR + tenant list from backend.    */
/* -------------------------------------------------------------------------- */

function Section({ icon: Icon, title, eyebrow, children }) {
  return (
    <section className="pal-card pal-grid-bg p-6 lg:p-7">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[rgba(174,82,234,0.10)] text-[var(--ve-violet-bright)] border border-[rgba(174,82,234,0.40)] shrink-0">
          <Icon size={16} />
        </span>
        <div className="flex-1 min-w-0">
          {eyebrow && <div className="pal-bracket">[{eyebrow.toUpperCase()}]</div>}
          <h3 className="display text-xl mt-1">{title}</h3>
        </div>
      </div>
      <div className="mt-5 text-sm leading-relaxed opacity-85 space-y-3">
        {children}
      </div>
    </section>
  );
}

function StatCard({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="pal-card pal-grid-bg p-5">
      <div className="pal-bracket">[{label.toUpperCase()}]</div>
      <div className="pal-mono text-2xl tabular-nums font-bold mt-2">{value}</div>
      {sub && <div className="pal-mono text-[10px] opacity-55 mt-1">{sub}</div>}
    </div>
  );
}

function fmtQAR(n) {
  if (n == null || isNaN(n)) return 'QAR 0';
  if (n >= 1000) return `QAR ${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return `QAR ${n.toFixed(0)}`;
}

const WHITELABEL_CAN = [
  'Run their own entities, agreements, approvals, board minutes',
  'Upload + auto-OCR documents (Gemini Vision)',
  'Chat with all 26 AI advisors (read-only — cannot retrain them)',
  'Use the AI Council to deliberate (their tenant only)',
  'Receive weekly Yusuf board briefs + Monday journal digests',
  'Manage their own users, roles, PTO, branding (logo + name)',
];

const WHITELABEL_CANNOT = [
  'Edit AI persona prompts or doctrines',
  'Trigger AI training cycles or speed-train mentors',
  'Run cross-tenant audit (their audit log is scoped to their tenant only)',
  'Use the role impersonator',
  'Build custom roles (phase 2 — platform owner only)',
  'View the platform MRR or other tenants',
];

const ACCESS_TIERS = [
  { tier: 'Platform Owner (system_admin)', who: 'M3 / Vigilant operations team', sees: 'Every tenant. Every audit log. AI training. MRR. Sub-domain provisioning.' },
  { tier: 'Holding Admin', who: 'Tenant owner (e.g. an incubator, a clinic group, a sovereign agency)', sees: 'Their tenant only. Full feature set minus AI training + cross-tenant audit.' },
  { tier: 'Approver chain (chairman / ceo / cfo / legal)', who: 'Board-level decision makers within a tenant', sees: 'Entities, approvals, board minutes, AI advisors, the artefacts they approve.' },
  { tier: 'Investor', who: 'External vetted capital partner', sees: 'Vetted startup opportunities, agreements they are party to, AI advisor chat.' },
  { tier: 'Founder', who: 'A startup being incubated', sees: 'Their own startup journey only. Founder gated 6-step workflow.' },
];

export default function OperatingManual() {
  const { user } = useAuth();
  const realRole = user?.real_role || user?.role;
  const isSystemAdmin = realRole === 'system_admin';

  const [mrr, setMrr] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [showFinancials, setShowFinancials] = useState(true);

  useEffect(() => {
    if (!isSystemAdmin) return;
    api.get('/admin/mrr').then(({ data }) => setMrr(data)).catch(() => {});
    api.get('/admin/tenants/summary').then(({ data }) => setTenants(data?.tenants || [])).catch(() => {});
  }, [isSystemAdmin]);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8 space-y-6" data-testid="operating-manual">
      {/* Header */}
      <header className="space-y-2">
        <div className="pal-bracket text-[#5E1F8B] dark:text-[#8B3FC5]">Operating manual · v1</div>
        <h1 className="display text-3xl sm:text-4xl">How Vigilant Entities works.</h1>
        <p className="text-sm text-slate-600 dark:text-white/60 max-w-3xl">
          A field guide for tenants, sub-tenants, and the platform owner. Explains who sees what, the white-label model, and the recurring-revenue dynamics underneath.
        </p>
      </header>

      {/* MRR (admin only) */}
      {isSystemAdmin && (
        <Section icon={Zap} title="Revenue posture" eyebrow="MRR · platform-owner view">
          <div className="flex items-center justify-between -mt-2">
            <p>Live ledger of paying tenants. Updates whenever the Stripe webhook records a successful subscription event.</p>
            <button
              onClick={() => setShowFinancials((v) => !v)}
              className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] inline-flex items-center gap-1.5"
              data-testid="mrr-toggle-visibility"
            >
              {showFinancials ? <Eye size={13} /> : <EyeOff size={13} />}
              {showFinancials ? 'Hide' : 'Reveal'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <StatCard
              testId="mrr-active"
              label="Active paid"
              value={showFinancials ? (mrr?.active_paid_tenants ?? '—') : '••'}
              sub="non-trial tenants"
            />
            <StatCard
              testId="mrr-trial"
              label="Trial"
              value={showFinancials ? (mrr?.trial_tenants ?? '—') : '••'}
              sub="in 7-day window"
            />
            <StatCard
              testId="mrr-monthly"
              label="MRR"
              value={showFinancials ? fmtQAR(mrr?.monthly_recurring_qar) : 'QAR ••'}
              sub="monthly recurring"
            />
            <StatCard
              testId="mrr-arr"
              label="ARR"
              value={showFinancials ? fmtQAR(mrr?.annual_run_rate_qar) : 'QAR ••'}
              sub="annualised"
            />
          </div>
          {showFinancials && mrr?.by_plan?.length > 0 && (
            <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 dark:border-white/10">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-white/[0.03] text-slate-500 dark:text-white/45 uppercase tracking-[0.18em]">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Plan</th>
                    <th className="text-left px-4 py-2.5 font-medium">Billing</th>
                    <th className="text-right px-4 py-2.5 font-medium">Tenants</th>
                    <th className="text-right px-4 py-2.5 font-medium">Monthly</th>
                    <th className="text-right px-4 py-2.5 font-medium">Annual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                  {mrr.by_plan.map((row) => (
                    <tr key={row.plan_id} data-testid={`mrr-row-${row.plan_id}`}>
                      <td className="px-4 py-2.5 font-medium">{row.plan_name}</td>
                      <td className="px-4 py-2.5 text-slate-500 dark:text-white/55 capitalize">{row.billing}</td>
                      <td className="px-4 py-2.5 text-right">{row.count}</td>
                      <td className="px-4 py-2.5 text-right">{fmtQAR(row.monthly_revenue_qar)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtQAR(row.annual_revenue_qar)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {/* Tenancy model */}
      <Section icon={Building2} title="Tenancy model" eyebrow="How clients live on the platform">
        <p>
          Vigilant Entities is multi-tenant. The platform itself is operated by <strong>M3 Holdings (the primary tenant)</strong>. Every paying customer — whether an incubator, a clinic group, a sovereign agency, or another holding company — becomes a <strong>white-label sub-tenant</strong>.
        </p>
        <p>
          Each sub-tenant gets a dedicated sub-domain (e.g. <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-xs">acme.vigilantentities.com</code>) and may upload their own logo + brand name. From their users&rsquo; perspective the system looks like <em>their</em> platform — same menu, same AI advisors, same workflows — but the deep configuration (AI persona prompts, training cycles, cross-tenant audit, role builder) stays under M3&rsquo;s control.
        </p>
        <p className="text-xs text-slate-500 dark:text-white/50">
          DNS provisioning is handled out-of-band today (Cloudflare wildcard CNAME → ingress). Roadmap: one-click sub-domain via Vercel/Cloudflare API.
        </p>
      </Section>

      {/* White-label CAN/CANT */}
      <Section icon={Shield} title="White-label boundaries" eyebrow="What sub-tenants can / cannot do">
        <div className="grid md:grid-cols-2 gap-4 -mt-1">
          <div className="rounded-lg border border-emerald-200/50 dark:border-emerald-400/20 bg-emerald-50/40 dark:bg-emerald-900/10 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300 mb-2">Sub-tenants CAN</div>
            <ul className="text-xs space-y-1.5 text-slate-700 dark:text-white/75">
              {WHITELABEL_CAN.map((line) => (
                <li key={line} className="flex gap-2"><span className="text-emerald-600 dark:text-emerald-400 shrink-0">✓</span><span>{line}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-rose-200/50 dark:border-rose-400/20 bg-rose-50/40 dark:bg-rose-900/10 p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-rose-700 dark:text-rose-300 mb-2">Sub-tenants CANNOT</div>
            <ul className="text-xs space-y-1.5 text-slate-700 dark:text-white/75">
              {WHITELABEL_CANNOT.map((line) => (
                <li key={line} className="flex gap-2"><span className="text-rose-600 dark:text-rose-400 shrink-0">✗</span><span>{line}</span></li>
              ))}
            </ul>
          </div>
        </div>
        <p className="text-xs text-slate-500 dark:text-white/50 mt-3">
          Enforcement: backend <code>permissions.is_whitelabel(user)</code> strips <code>TRAIN_AI_MENTORS</code>, <code>VIEW_AUDIT_CROSS_TENANT</code>, <code>MANAGE_ROLES</code>, and <code>IMPERSONATE_ROLES</code> from every non-primary tenant&rsquo;s permission set. The frontend nav hides the corresponding menu items.
        </p>
      </Section>

      {/* Access tiers */}
      <Section icon={Users} title="Access tiers" eyebrow="Who sees what, by role">
        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-white/10 -mt-1">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-white/[0.03] text-slate-500 dark:text-white/45 uppercase tracking-[0.18em]">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Tier</th>
                <th className="text-left px-4 py-2.5 font-medium">Who</th>
                <th className="text-left px-4 py-2.5 font-medium">Sees</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {ACCESS_TIERS.map((row) => (
                <tr key={row.tier}>
                  <td className="px-4 py-3 font-medium align-top">{row.tier}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-white/55 align-top">{row.who}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-white/75 align-top">{row.sees}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* AI control */}
      <Section icon={Sparkles} title="AI control plane" eyebrow="Why the 26 advisors stay under M3">
        <p>
          The 26 prophet-named AI advisors are a competitive moat. They age <strong>+10 years per day</strong> (currently at 1,010y of mastery) through Shu&rsquo;aib&rsquo;s training cycle, are emotionally calibrated by Wujdān, and are anchored in Qatar NDS3 strategic context by Idris.
        </p>
        <p>
          Allowing every sub-tenant to fork or retrain the personas would (a) destroy the consistency of advice across the network, (b) let one bad-actor poison the shared model, and (c) erode the differentiation that justifies the platform&rsquo;s price. So <strong>training is gated to M3 (system_admin)</strong>. Sub-tenants get to <em>consume</em> the AI — chat, council, drafting, OCR — without the controls to retrain it.
        </p>
      </Section>

      {/* Sovereign-AI Sovereignty layer — v92+ */}
      <Section icon={ShieldCheck} title="Sovereign-AI architecture" eyebrow="How tenant data stays tenant data">
        <p>
          Every outbound LLM call in the platform — AI Council, Iqra journal, Yusuf Monday brief, Wujdān cross-pollination, Shu&rsquo;aib mentorship, every persona chat — runs through a single hardened path: <code>llm_helpers.chat_with_fallback()</code>. That function enforces three guarantees before the prompt leaves the cluster:
        </p>
        <ol className="list-decimal pl-4 space-y-1.5 text-xs">
          <li><strong>Auto-redaction.</strong> Nine built-in PII patterns (email · phone · URL · social handle · Qatari ID · IBAN · US SSN · credit card · passport) plus every tenant-defined trade-secret token are scrubbed from the prompt. Configure tokens in <a className="underline" href="/dashboard/privacy">/dashboard/privacy</a>.</li>
          <li><strong>Routing receipt.</strong> Each call records {`{provider, model, region, latency_ms, ok}`} on the council session document. Lets auditors prove which sub-provider (anthropic / openai / gemini / bedrock) answered each persona.</li>
          <li><strong>Provider opt-out.</strong> Every external provider in our fallback chain (Anthropic, OpenAI, Gemini) is contractually opted out of training. When the tenant connects their own AWS, prompts route via Amazon Bedrock and never leave their region. Data-plane sovereignty is a separate opt-in: connect Microsoft Azure at <a className="underline" href="/dashboard/admin/azure">/dashboard/admin/azure</a> and tenant application data is written to your own Azure Cosmos DB subscription instead of our managed cluster.</li>
        </ol>
        <p>
          The aggregate of all routing receipts is the <strong>Sovereignty Score</strong>: the % of LLM calls in the last 30 days that ran inside the tenant&rsquo;s own AWS. Visible on <a className="underline" href="/dashboard/settings/aws">/dashboard/settings/aws</a> · regenerated on every load.
        </p>
      </Section>

      <Section icon={Cloud} title="Bring-your-own-cloud (BYOC) flow" eyebrow="Tenant connects their own AWS">
        <ol className="list-decimal pl-4 space-y-1.5 text-xs">
          <li>Tenant admin pastes their AWS IAM Role ARN + external-id on <code>/dashboard/settings/aws</code> (per-tenant) or <code>/dashboard/admin/aws</code> (platform-wide).</li>
          <li>Selects which AWS services to enable (Bedrock · KMS · S3 · SES · Athena · Secrets Manager).</li>
          <li>If Bedrock is enabled, picks a model from the 9-item catalogue (Claude 3.5 Sonnet / Llama 3.1 70B / Mistral Large 2 / Nova Pro / ...).</li>
          <li>Hits VERIFY NOW → we run STS:AssumeRole + GetCallerIdentity. Status flips to <code>connected</code>.</li>
          <li>From that moment, every LLM call for that tenant routes through their Bedrock model. The Sovereignty Score climbs daily.</li>
          <li>Nightly @ 02:30 UTC, an automated health sweep probes every enabled service and emails the tenant owner on any new failure.</li>
        </ol>
      </Section>

      <Section icon={Activity} title="Privacy audit trail" eyebrow="What admins can prove to regulators">
        <p>
          Three live surfaces back up the marketing copy with hard evidence:
        </p>
        <ul className="list-disc pl-4 space-y-1.5 text-xs">
          <li><strong>Recent Redactions Feed</strong> — last 50 events (counts only, samples are bullet-masked even server-side). At <code>/dashboard/privacy</code>.</li>
          <li><strong>Council Routing Receipts</strong> — per-deliberation log persisted on every <code>ai_council_sessions</code> document. View at <code>/dashboard/ai-council/{`{council_id}`}</code>.</li>
          <li><strong>Sovereignty Score sparkline</strong> — 30-day daily trend at the top of the AWS Connection page.</li>
          <li><strong>Provider Policy Matrix</strong> — declarative training-use status for every LLM provider, served from the backend so changes propagate everywhere. Visible on both <code>/dashboard/privacy</code> and the public <code>/security</code> page.</li>
        </ul>
      </Section>

      {/* Sub-domain workflow */}
      <Section icon={Lock} title="Sub-domain provisioning" eyebrow="How a new tenant goes live">
        <ol className="list-decimal pl-4 space-y-1.5 text-xs">
          <li>Prospect signs up via <code>/start-trial</code> → 7-day free trial as a new white-label tenant.</li>
          <li>Tenant uploads logo + brand name → <code>POST /api/tenants/onboard</code>.</li>
          <li>M3 ops issues a Cloudflare wildcard CNAME for <code>{'{tenant}'}.vigilantentities.com</code> → ingress routes by Host header.</li>
          <li>Tenant converts to paid: chooses <strong>Monthly (QAR 10k)</strong> or <strong>Annual (QAR 75k, ~37% off)</strong>.</li>
          <li>Stripe webhook flips <code>tenant.plan</code> from <code>trial</code> → <code>monthly</code>/<code>annual</code>. MRR counter ticks up.</li>
          <li>Resale guard: a tenant <em>cannot</em> onboard another tenant. Only M3 (system_admin) can.</li>
        </ol>
      </Section>

      {/* Tenant list (admin only) */}
      {isSystemAdmin && tenants.length > 0 && (
        <Section icon={Building2} title="Tenant ledger" eyebrow={`${tenants.length} tenants on file`}>
          <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-white/10 -mt-1">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-white/[0.03] text-slate-500 dark:text-white/45 uppercase tracking-[0.18em]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Tenant</th>
                  <th className="text-left px-4 py-2.5 font-medium">Brand</th>
                  <th className="text-left px-4 py-2.5 font-medium">Plan</th>
                  <th className="text-left px-4 py-2.5 font-medium">Sub-domain</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-left px-4 py-2.5 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {tenants.slice(0, 25).map((t) => (
                  <tr key={t.tenant_id} data-testid={`tenant-row-${t.tenant_id}`}>
                    <td className="px-4 py-2.5 font-mono text-[11px]">{t.tenant_id}</td>
                    <td className="px-4 py-2.5">{t.brand_name || '—'}</td>
                    <td className="px-4 py-2.5 capitalize">{t.plan || 'trial'}</td>
                    <td className="px-4 py-2.5">
                      <SubdomainCell tenant={t} onChange={(s) => {
                        setTenants((ts) => ts.map((x) => x.tenant_id === t.tenant_id ? { ...x, subdomain: s } : x));
                      }} />
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${t.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-white/40'}`}>
                        {t.is_active ? 'active' : 'inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 dark:text-white/55">{(t.created_at || '').slice(0, 10) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Footer */}
      <Section icon={BookOpen} title="Need to extend this?" eyebrow="Hooks for builders">
        <ul className="list-disc pl-4 space-y-1.5 text-xs">
          <li>Permission registry: <code>/app/backend/permissions.py</code></li>
          <li>White-label gate: <code>permissions.is_whitelabel(user)</code></li>
          <li>MRR endpoint: <code>GET /api/admin/mrr</code> (system_admin only)</li>
          <li>Permission API: <code>GET /api/auth/me/permissions</code> — returns the user&rsquo;s live permission set</li>
          <li>Sub-domain provisioning: <code>POST /api/tenants/&#123;id&#125;/subdomain</code> (app-layer, requires wildcard CNAME at DNS provider)</li>
        </ul>
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  SubdomainCell — inline editor in the Tenant ledger                        */
/* -------------------------------------------------------------------------- */
function SubdomainCell({ tenant, onChange }) {
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState(tenant.subdomain || '');
  const [busy, setBusy] = useState(false);
  const baseDomain = 'vigilantentities.com';

  const save = async () => {
    if (!slug.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenant.tenant_id}/subdomain`, { subdomain: slug.trim().toLowerCase() });
      toast.success(`Provisioned ${data.fqdn}`);
      onChange(data.subdomain);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not provision.');
    } finally { setBusy(false); }
  };

  const release = async () => {
    if (!window.confirm(`Release "${tenant.subdomain}.${baseDomain}"? Cached redirects may misroute.`)) return;
    setBusy(true);
    try {
      await api.delete(`/tenants/${tenant.tenant_id}/subdomain`);
      toast.success('Released.');
      onChange(null);
      setSlug('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not release.');
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1.5">
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="acme"
          data-testid={`subdomain-input-${tenant.tenant_id}`}
          className="w-24 px-2 py-1 text-[11px] font-mono rounded border border-[#8B3FC5]/50 bg-white dark:bg-white/5 focus:outline-none focus:border-[#8B3FC5]"
        />
        <span className="text-[10px] text-slate-400 dark:text-white/40">.{baseDomain.replace('vigilantentities.com', 've.com')}</span>
        <button
          onClick={save}
          disabled={busy || !slug.trim()}
          data-testid={`subdomain-save-${tenant.tenant_id}`}
          className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-1 rounded bg-[#8B3FC5] text-white hover:bg-[#AE52EA] disabled:opacity-50 transition"
        >
          <Check size={11} />
        </button>
        <button
          onClick={() => { setEditing(false); setSlug(tenant.subdomain || ''); }}
          className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-1 rounded border border-slate-200 dark:border-white/10 text-slate-500"
        >
          ×
        </button>
      </div>
    );
  }

  if (tenant.subdomain) {
    return (
      <div className="inline-flex items-center gap-2">
        <span
          data-testid={`subdomain-display-${tenant.tenant_id}`}
          className="inline-flex items-center gap-1.5 text-[11px] font-mono text-[#5E1F8B] dark:text-[#8B3FC5]"
        >
          <Globe size={10} />
          {tenant.subdomain}<span className="text-slate-400 dark:text-white/40">.{baseDomain}</span>
        </span>
        <button
          onClick={() => setEditing(true)}
          data-testid={`subdomain-edit-${tenant.tenant_id}`}
          className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-white/55 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5]"
        >
          edit
        </button>
        <button
          onClick={release}
          disabled={busy}
          className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-600 hover:text-rose-700"
        >
          release
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      data-testid={`subdomain-provision-${tenant.tenant_id}`}
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 dark:text-white/55 hover:text-[#5E1F8B] dark:hover:text-[#8B3FC5] transition"
    >
      <Globe size={10} /> Provision
    </button>
  );
}
